import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  Typography,
  Button,
  Spin,
  Table,
  Tag,
  Statistic,
} from 'antd';
import {
  ShoppingCartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
// Check if fetchUserOrders exists, if not use fetchOrders with userId filter or similar
import { fetchOrders } from '../../store/slices/ordersSlice';
import ErrorMessage from '../common/ErrorMessage';

const { Title } = Typography;

const SellerDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orders, loading, error } = useSelector((state) => state.orders);
  const user = useSelector((state) => state.auth.user);

  useEffect(() => {
    if (user && user.role === 'seller') {
      // Assuming fetchOrders handles seller filtering or we pass a filter
      dispatch(fetchOrders({ status: 'pending' }));
    }
  }, [dispatch, user]);

  if (loading && !orders.length) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  const pendingOrders = orders.filter((order) => order.status === 'pending');
  const confirmedOrders = orders.filter((order) => order.status === 'confirmed');
  const completedOrders = orders.filter((order) => order.status === 'completed');
  const cancelledOrders = orders.filter((order) => order.status === 'cancelled');

  const stats = [
    {
      title: 'Pending Orders',
      value: pendingOrders.length,
      icon: <ClockCircleOutlined style={{ fontSize: 32, color: '#fa8c16' }} />,
      color: '#fa8c16',
    },
    {
      title: 'Confirmed Orders',
      value: confirmedOrders.length,
      icon: <CheckCircleOutlined style={{ fontSize: 32, color: '#52c41a' }} />,
      color: '#52c41a',
    },
    {
      title: 'Completed',
      value: completedOrders.length,
      icon: <ShoppingCartOutlined style={{ fontSize: 32, color: '#1890ff' }} />,
      color: '#1890ff',
    },
    {
      title: 'Cancelled',
      value: cancelledOrders.length,
      icon: <CloseCircleOutlined style={{ fontSize: 32, color: '#f5222d' }} />,
      color: '#f5222d',
    },
  ];

  const columns = [
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
      render: (id) => `#${id}`,
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_, record) => (
        record.User ? `${record.User.first_name} ${record.User.last_name}` : 'N/A'
      ),
    },
    {
      title: 'Items',
      key: 'items',
      render: (_, record) => (
        record.OrderItems ? `${record.OrderItems.length} items` : '0 items'
      ),
    },
    {
      title: 'Total',
      key: 'total',
      render: (_, record) => (
        `${(record.total_amount || 0).toLocaleString()} UZS`
      ),
    },
    {
      title: 'Status',
      key: 'status',
      dataIndex: 'status',
      render: (status) => (
        <Tag color="warning" style={{ textTransform: 'capitalize' }}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          onClick={() => navigate(`/seller/orders/${record.id}`)}
        >
          Manage
        </Button>
      ),
      align: 'right',
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>Seller Dashboard</Title>
        <Button
          type="primary"
          onClick={() => navigate('/seller/pending-orders')}
        >
          View All Orders
        </Button>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
        {stats.map((stat, index) => (
          <Col xs={24} sm={12} md={6} key={index}>
            <Card bordered={false} bodyStyle={{ padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Statistic title={stat.title} value={stat.value} />
                {stat.icon}
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Pending Orders Table */}
      <Card title="Recent Pending Orders" bordered={false}>
        <Table
          columns={columns}
          dataSource={pendingOrders.slice(0, 5)}
          rowKey="id"
          pagination={false}
          locale={{ emptyText: 'No pending orders' }}
        />
      </Card>
    </div>
  );
};

export default SellerDashboard;
